/**
 * Shared logic for the wizard's five-box "prior tax-year income" breakdown
 * (salary / drawdown / State Pension / rental / dividends), used by every
 * surface that reads or derives it: the desktop wizard (Step2People.tsx),
 * the mobile wizard (Step2People.mobile.tsx), the rebaseline "Update my
 * plan" flow (UpdatePlanFlow.tsx), StepReview.tsx and planDiff.ts.
 *
 * Salary, drawdown, State Pension and rental are ordinary non-savings
 * income taxed identically, so all four sum into the single combined figure
 * the engine reads (`priorOtherTaxableIncome`). Dividends are kept apart —
 * see sumPriorIncomeBreakdown below.
 *
 * This feature had four separate bugs with the same root cause: the
 * breakdown and its derived sum (`priorOtherTaxableIncome`) were BOTH
 * persisted, and every bug was that pair going out of sync with no way to
 * tell which was stale. Phase 3 removed the second copy entirely — the
 * breakdown is the only thing ever persisted; the total is always derived,
 * here or in the server's equivalent logic (mergeRebaselineUpdates /
 * PersonSchema in projectionController.ts). There is deliberately no
 * "reconcile drift between the two" function any more — with one stored
 * value, there is nothing to drift.
 */

import type { Step2FormData } from '../types/wizard';

/** Coerces undefined/null/NaN to 0; passes any other finite number through unchanged. */
export function numberOrZero(value: number | null | undefined): number {
  if (value === undefined || value === null) return 0;
  return Number.isNaN(value) ? 0 : value;
}

/**
 * The single answer to "is this breakdown field present at all?", shared by
 * every surface that asks it (this file's deriveTotalPriorOtherIncome, and
 * the server's derivePriorOtherTaxableIncome / mergeRebaselineUpdates in
 * projectionController.ts). `null` and `undefined` must count as absent
 * identically — a blanked currency box leaves react-hook-form holding NaN,
 * which `JSON.stringify` turns into `null` on the round-trip (see
 * seedPriorDrawdownIncome above). Before this was unified, three call sites
 * each re-implemented this test and two of them treated `null` as PRESENT,
 * which either summed a blanked box to a silent 0 or deleted a legit stored
 * total (#1592 phase 2 review round 4).
 *
 * Note this is a different question from seedPriorDrawdownIncome's
 * null-vs-undefined distinction, which deliberately treats them
 * differently to tell "never set" apart from "explicitly cleared" for ONE
 * field. This helper answers "does the breakdown exist at all", where null
 * and undefined both mean no.
 */
export function isBreakdownFieldPresent(value: number | null | undefined): boolean {
  return value !== undefined && value !== null;
}

/**
 * The engine only takes ONE summed non-dividend field
 * (`priorOtherTaxableIncome`) — salary, drawdown, State Pension and rental
 * are all ordinary non-savings income taxed identically. Asking users to
 * sum four figures in their head risks a wrong subtotal silently
 * mis-banding their Personal Allowance, so every surface collects them
 * separately and sums here instead of hand-rolling `a + b + c + d` four
 * times over (HIGH 1, #1592 phase 2 review round 3).
 *
 * NaN-safe per field, not just at the end: a blanked currency box leaves
 * react-hook-form holding NaN for THAT field only, and `??` does not catch
 * NaN (it only catches null/undefined). Summing raw `a + b + c + d` turns
 * one blanked box into a NaN *total*, which a later NaN-scrub (sanitizeNaN)
 * would then silently zero out entirely — losing every other box's real
 * income, not just the blanked one. Treating each field's NaN as 0 before
 * summing means a blanked box contributes nothing, instead of poisoning the
 * whole subtotal.
 */
export function sumPriorIncomeBreakdown(
  priorSalaryIncome: number | null | undefined,
  priorDrawdownIncome: number | null | undefined,
  priorStatePensionIncome: number | null | undefined,
  priorRentalIncome: number | null | undefined
): number {
  return (
    numberOrZero(priorSalaryIncome) +
    numberOrZero(priorDrawdownIncome) +
    numberOrZero(priorStatePensionIncome) +
    numberOrZero(priorRentalIncome)
  );
}

/**
 * Seeding rule shared by every surface that pre-fills the wizard's
 * "Private pension income" breakdown box from a stored plan (#1592 phase 2 review
 * round 2, CRITICAL 2).
 *
 * A plan saved before the drawdown/State Pension/rental split existed only
 * has the combined `priorOtherTaxableIncome` total — that legacy total
 * should seed the Drawdown box so the running total still matches what was
 * there before, rather than silently dropping it (see Step2People.tsx's
 * defaultPersons comment).
 *
 * But `??` cannot tell "this key was never set" (legacy plan — seed from the
 * total) apart from "this key was explicitly cleared to nothing" (the user
 * blanked the box). Both look identical to `??`, yet they must behave
 * differently: a genuinely cleared box must stay 0, not silently re-seed
 * from the legacy total and double the running total the next time the box
 * opens. The two cases only differ in whether the key is `undefined` at all
 * — `null` (what a blanked currency box's NaN becomes after a JSON
 * round-trip) counts as "explicitly present", same as any real number.
 */
export function seedPriorDrawdownIncome(
  priorDrawdownIncome: number | null | undefined,
  legacyPriorOtherTaxableIncome: number | null | undefined
): number {
  if (priorDrawdownIncome !== undefined) return numberOrZero(priorDrawdownIncome);
  return numberOrZero(legacyPriorOtherTaxableIncome);
}

/**
 * Derives the figure to DISPLAY/DIFF for a person's non-dividend prior
 * income from whichever the plan actually has on it — the wizard's
 * four-box breakdown (current-format plans, and any plan that has ever
 * been through Step 2 since the split), or the legacy combined total
 * (a plan saved before the split existed, or written directly by a
 * non-wizard API/MCP caller that only ever sends the combined figure).
 *
 * This is the read-side counterpart to the server's mergeRebaselineUpdates
 * "breakdown present -> derive; otherwise -> bare total" rule (phase 3,
 * derived-total refactor): the combined total is no longer PERSISTED
 * anywhere breakdown fields also exist, so any surface that used to read a
 * stored `priorOtherTaxableIncome` directly (StepReview.tsx, planDiff.ts)
 * must derive it here instead, or it will silently show 0 for every plan
 * that has a breakdown once the stored total stops being written.
 *
 * "Breakdown present" means at least one of the four fields is present per
 * isBreakdownFieldPresent — `undefined`/`null` are the only signals that a
 * plan predates the breakdown entirely (see seedPriorDrawdownIncome's
 * undefined-vs-null distinction, which is a different question); 0 counts
 * as present, since a wizard-format plan always has real numbers here
 * (Step2Schema defaults each box to 0).
 */
export function deriveTotalPriorOtherIncome(
  priorSalaryIncome: number | null | undefined,
  priorDrawdownIncome: number | null | undefined,
  priorStatePensionIncome: number | null | undefined,
  priorRentalIncome: number | null | undefined,
  legacyPriorOtherTaxableIncome: number | null | undefined
): number {
  const breakdownPresent =
    isBreakdownFieldPresent(priorSalaryIncome) ||
    isBreakdownFieldPresent(priorDrawdownIncome) ||
    isBreakdownFieldPresent(priorStatePensionIncome) ||
    isBreakdownFieldPresent(priorRentalIncome);
  if (breakdownPresent) {
    return sumPriorIncomeBreakdown(priorSalaryIncome, priorDrawdownIncome, priorStatePensionIncome, priorRentalIncome);
  }
  return numberOrZero(legacyPriorOtherTaxableIncome);
}

/**
 * Removes the derived `priorOtherTaxableIncome` field before a Step 2 save
 * or submission (phase 3, derived-total refactor), on both the desktop
 * (Step2People.tsx) and mobile (Step2People.mobile.tsx) wizard. It must
 * never reach the saved plan document or the API payload — whatever the
 * form currently holds for it (a stale legacy seed, or Step2Schema's
 * `.default(0)`) is not the source of truth; the three breakdown boxes
 * are. The server derives the combined figure itself from whichever the
 * request actually carries (PersonSchema/mergeRebaselineUpdates in
 * projectionController.ts) — stripping it here, rather than sending an
 * explicit value, keeps that server-side derivation authoritative instead
 * of racing this client copy.
 */
export function stripDerivedPriorIncomeTotal(data: Step2FormData): Step2FormData {
  return {
    ...data,
    persons: data.persons.map(({ priorOtherTaxableIncome: _priorOtherTaxableIncome, ...rest }) => rest),
  };
}
